import { Box, Chip, Grid, Paper, Typography } from '@mui/material';
import { AppFormikTextField } from 'shared/components/AppComponents';
import { ReactComponent as Plus } from 'assets/images/plus_role_not.svg';
import { ReactComponent as PlusFill } from 'assets/images/plus_role.svg';
import { useFormikContext } from 'formik';
import { useEffect, useRef } from 'react';

interface AchievementFormProps {
  test?: any;
  purpose?: any;
  errorMessageContent?: any;
  errorMessagePrerequisites?: any;
  errorMessageObjectives?: any;
}

const TrainingContent: React.FC<AchievementFormProps> = ({
  purpose = null,
  errorMessageContent = null,
  errorMessagePrerequisites = null,
  errorMessageObjectives = null,
}: any) => {
  const { values, setFieldValue, errors, submitCount }: any =
    useFormikContext();
  const contentRef = useRef<HTMLDivElement | null>(null);
  const contentRefField =
    purpose === 'external'
      ? ['content', 'prerequisites', 'learning_objectives', 'duration_minutes']
      : ['content', 'prerequisites', 'learning_objectives'];

  const handleAddCustomPrerequisites = (): void => {
    const custom = values.add_prerequisites?.trim();
    if (!custom) return;

    const existing: string[] = values.prerequisites ?? [];

    if (!existing.includes(custom)) {
      setFieldValue('prerequisites', [...existing, custom]);
    }

    setFieldValue('add_prerequisites', '');
  };

  const handleDeletePrerequisite = (itemToDelete: string): void => {
    const existing: string[] = values.prerequisites ?? [];

    const updated = existing.filter((item) => item !== itemToDelete);

    setFieldValue('prerequisites', updated);
  };

  // Objectives

  const handleAddCustomLearningObjectives = (): void => {
    const custom = values.add_learning_objectives?.trim();
    if (!custom) return;

    const existing: string[] = values.learning_objectives ?? [];

    if (!existing.includes(custom)) {
      setFieldValue('learning_objectives', [...existing, custom]);
    }

    setFieldValue('add_learning_objectives', '');
  };

  const handleDeleteLearningObjectives = (itemToDelete: string): void => {
    const existing: string[] = values.learning_objectives ?? [];

    const updated = existing.filter((item) => item !== itemToDelete);

    setFieldValue('learning_objectives', updated);
  };

  useEffect(() => {
    if (submitCount === 0) return;
    if (!errors || Object.keys(errors).length === 0) return;

    const contentError = contentRefField.some((field) => errors?.[field]);
    if (contentError) {
      contentRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [errors, submitCount]);

  return (
    <Paper variant="outlined" className="achievement-form-wrapper">
      <Typography variant="h6">
        Prerequisites & Learning Objectives{' '}
        <span
          style={{
            color: '#d32f2f',
            fontSize: '10px',
            fontWeight: 400,
          }}
        >
          {errorMessageContent ? errorMessageContent : ''}
        </span>
      </Typography>

      {/* -------------Prerequisites */}
      <Box ref={contentRef}>
        <Typography mt={3}>Prerequisites</Typography>
        <Grid
          container
          className="achievement-target-grid"
          sx={{ alignItems: 'start' }}
          mt={2}
        >
          <Grid item md={11}>
            <AppFormikTextField
              name="add_prerequisites"
              placeholder="Add prerequisites"
            />
            <Typography
              sx={{
                color: '#d32f2f',
                fontSize: '10px',
                fontWeight: 400,
              }}
            >
              {errorMessagePrerequisites ? errorMessagePrerequisites : ''}
            </Typography>
          </Grid>
          <Grid item md={1}>
            <Box
              onClick={handleAddCustomPrerequisites}
              sx={{ cursor: values.add_prerequisites ? 'pointer' : '' }}
            >
              {values.add_prerequisites ? <PlusFill /> : <Plus />}
            </Box>
          </Grid>
        </Grid>

        {values &&
          values.prerequisites.map((data: any) => (
            <Chip
              sx={{
                background: '#F1F5F4',
                borderRadius: '48px',
                margin: '5px 0',
              }}
              label={data}
              size="small"
              onDelete={() => handleDeletePrerequisite(data)}
            />
          ))}
      </Box>

      {/* -------------Learning objectives */}
      <Box>
        {' '}
        <Typography mt={3}>Learning objectives</Typography>
        <Grid
          container
          className="achievement-target-grid"
          sx={{ alignItems: 'start' }}
          mt={2}
        >
          <Grid item md={11}>
            <AppFormikTextField
              name="add_learning_objectives"
              placeholder="Add learning objective"
            />
            <Typography
              sx={{
                color: '#d32f2f',
                fontSize: '10px',
                fontWeight: 400,
              }}
            >
              {errorMessageObjectives ? errorMessageObjectives : ''}
            </Typography>
          </Grid>
          <Grid item md={1}>
            <Box
              onClick={handleAddCustomLearningObjectives}
              sx={{ cursor: values.add_learning_objectives ? 'pointer' : '' }}
            >
              {values.add_learning_objectives ? <PlusFill /> : <Plus />}
            </Box>
          </Grid>
        </Grid>
        {values &&
          values.learning_objectives.map((data: any) => (
            <Chip
              sx={{
                background: '#F1F5F4',
                borderRadius: '48px',
                margin: '5px 0',
              }}
              label={data}
              size="small"
              onDelete={() => handleDeleteLearningObjectives(data)}
            />
          ))}
      </Box>

      {purpose === 'external' && (
        <Box mt={3}>
          <AppFormikTextField
            name="duration_minutes"
            placeholder="Add training duration"
            label="Estimated Duration (minutes)"
            outerLabel
            type="number"
          />
        </Box>
      )}
    </Paper>
  );
};

export default TrainingContent;
